import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "../../state/authStore";
import { AuthLayout } from "./AuthLayout";

type VerifyState =
  | { kind: "checking" }
  | { kind: "success"; alreadyVerified: boolean }
  | { kind: "expired" }
  | { kind: "invalid" }
  | { kind: "network" }
  | { kind: "missing-token" };

/**
 * Public route at /verify?token=… — the destination of the verify link in
 * the welcome / reminder mails. Calls the auth.verify API on mount, then
 * shows success or the appropriate error state. Does NOT log the user in:
 * the verify link is for email-proof only; login happens through /login.
 */
export function VerifyPage(): JSX.Element {
  const consumeVerifyToken = useAuthStore((s) => s.consumeVerifyToken);
  const status = useAuthStore((s) => s.status);
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token") ?? "";

  const [state, setState] = useState<VerifyState>(() =>
    token ? { kind: "checking" } : { kind: "missing-token" },
  );

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      const result = await consumeVerifyToken(token);
      if (cancelled) return;
      if (result.ok) {
        setState({ kind: "success", alreadyVerified: result.alreadyVerified });
      } else if (result.reason === "expired") {
        setState({ kind: "expired" });
      } else if (result.reason === "already_used") {
        setState({ kind: "success", alreadyVerified: true });
      } else if (result.reason === "network") {
        setState({ kind: "network" });
      } else {
        setState({ kind: "invalid" });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, consumeVerifyToken]);

  if (state.kind === "missing-token") {
    return (
      <AuthLayout title="E-Mail bestätigen" subtitle="Link unvollständig.">
        <p className="text-sm text-fg-muted">
          Der Link ist unvollständig — es fehlt der <code className="font-mono">token</code>-Parameter.
          Bitte den Link aus der E-Mail komplett kopieren oder über das Banner in der App einen neuen anfordern.
        </p>
        <Link
          to={status === "authed" ? "/" : "/login"}
          className="block text-center min-h-[44px] py-2 rounded-lg text-accent hover:underline"
          data-testid="verify-back-link"
        >
          ← Zurück zur App
        </Link>
      </AuthLayout>
    );
  }

  if (state.kind === "checking") {
    return (
      <AuthLayout title="E-Mail bestätigen" subtitle="Einen Moment …">
        <p className="text-sm text-fg-muted animate-pulse" data-testid="verify-pending">
          Wir bestätigen gerade deine E-Mail-Adresse …
        </p>
      </AuthLayout>
    );
  }

  if (state.kind === "success") {
    return (
      <AuthLayout
        title="E-Mail bestätigt"
        subtitle={
          state.alreadyVerified
            ? "Du warst schon bestätigt — alles gut."
            : "Danke! Du bist jetzt vollständig eingerichtet."
        }
      >
        <p className="text-sm text-fg-muted" data-testid="verify-success">
          {state.alreadyVerified
            ? "Diese E-Mail-Adresse war bereits bestätigt. Du kannst jederzeit dein Passwort zurücksetzen."
            : "Deine E-Mail-Adresse ist jetzt verifiziert. Falls du dein Passwort vergisst, kommst du jederzeit per „Passwort vergessen?“ wieder rein."}
        </p>
        <button
          type="button"
          onClick={() => navigate(status === "authed" ? "/" : "/login", { replace: true })}
          data-testid="verify-continue"
          className="w-full min-h-[52px] rounded-xl bg-accent text-accent-on text-base font-semibold shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-outline-strong"
        >
          {status === "authed" ? "Weiter zur App" : "Zum Login"}
        </button>
      </AuthLayout>
    );
  }

  // expired / invalid / network — all share a similar UI with a different message.
  const message = (() => {
    if (state.kind === "expired") {
      return "Der Bestätigungslink ist abgelaufen. Logg dich kurz ein und fordere im Banner einen neuen Link an.";
    }
    if (state.kind === "network") {
      return "Keine Verbindung zum Server. Versuch's gleich noch einmal — oder logg dich ein und fordere im Banner einen neuen Link an.";
    }
    return "Der Bestätigungslink ist ungültig oder wurde bereits verbraucht. Logg dich ein und fordere im Banner einen neuen Link an.";
  })();

  return (
    <AuthLayout title="E-Mail bestätigen" subtitle="Das hat nicht geklappt.">
      <p className="text-sm text-danger" data-testid="verify-error">
        {message}
      </p>
      <Link
        to={status === "authed" ? "/" : "/login"}
        className="block text-center min-h-[44px] py-2 rounded-lg text-accent hover:underline"
        data-testid="verify-back-link"
      >
        ← Zurück
      </Link>
    </AuthLayout>
  );
}
